import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import type { Event, EventHint } from '@sentry/react';

// Error names/messages that describe a transient or already-handled condition rather
// than a defect. Reporting them buries real regressions: "Session expired" alone
// accounted for the majority of this project's Sentry volume (WA-3565, WA-3585) while
// the app was behaving exactly as designed (redirect back to login).
const EXPECTED_ERROR_NAMES = ['SessionExpiredError', 'NetworkUnavailableError', 'AbortError'];

const TRANSIENT_MESSAGES = [
  'Session expired',
  'Network request failed',
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource',
  'The user aborted a request',
];

/**
 * HTTP statuses that mean "try again", not "something is broken in the app":
 * an expired token and gateway/proxy hiccups in front of the federated API.
 * Read off the status rather than the message, because `graphql-request` formats
 * its `ClientError` text as `GraphQL Error (Code: 502): …`.
 */
const TRANSIENT_STATUSES = [401, 502, 503, 504];

/** True when the event only describes transient/expected behaviour and must not be reported. */
const isNoise = (event: Event, hint?: EventHint): boolean => {
  const original = hint?.originalException as Error | undefined;
  if (original?.name && EXPECTED_ERROR_NAMES.includes(original.name)) return true;

  const status = (original as { response?: { status?: number } } | undefined)?.response?.status;
  if (status !== undefined && TRANSIENT_STATUSES.includes(status)) return true;

  const values = event.exception?.values ?? [];
  if (values.some(({ type }) => type && EXPECTED_ERROR_NAMES.includes(type))) return true;

  const texts = [original?.message, event.message, ...values.map(({ value }) => value)];
  return texts.some(
    (text) => !!text && TRANSIENT_MESSAGES.some((message) => text.includes(message)),
  );
};

// Initializes Sentry once at app startup (called from src/index.tsx before the
// React tree is rendered). Mirrors the init pattern used across hssoft services
// (see console-hssoft-com-service-mono `src/shared/sentry.ts`): DSN-driven,
// enabled only in production, scoped per environment, with a modest trace
// sample rate.
export function initSentry() {
  const appEnv = process.env.REACT_APP_ENV || 'production';
  // Only report from the production build. Other envs → SDK is inert (no-ops).
  const enabled = appEnv === 'production';

  Sentry.init({
    dsn: 'https://d974735b1865adc3bca8b4cadaa5ff3a@o4504242163613696.ingest.us.sentry.io/4511569998577664',
    enabled,
    environment: appEnv,
    // Ties events to a release for tracking (REACT_APP_VERSION is injected at
    // build time by the `inject-version` npm script).
    release: process.env.REACT_APP_VERSION,
    integrations: [new BrowserTracing()],
    tracesSampleRate: 0.1,
    // Application errors and real GraphQL failures (400/500) stay visible; only the
    // transient and already-handled cases listed above are dropped.
    beforeSend: (event, hint) => (isNoise(event, hint) ? null : event),
  });
}

export { Sentry };
